import React, { useState, useEffect, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './CollapsibleCard.css';

interface CollapsibleCardProps {
    /** Unique identifier for this card - used for persistence */
    cardId: string;
    /** Title displayed in the card header */
    title: string;
    /** Content to display inside the card */
    children: ReactNode;
    /** Optional className for additional styling */
    className?: string;
    /** Whether the card should start collapsed (default: false) */
    defaultCollapsed?: boolean;
    /** Optional subtitle or description */
    subtitle?: string;
    /** Optional header actions (buttons, etc.) to display before the collapse toggle */
    headerActions?: ReactNode;
}

const STORAGE_KEY_PREFIX = 'card-state-';

export const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
    cardId,
    title,
    children,
    className = '',
    defaultCollapsed = false,
    subtitle,
    headerActions,
}) => {
    // Load initial state from localStorage safely
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const [isInitialized, setIsInitialized] = useState(false);

    // Load from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem(STORAGE_KEY_PREFIX + cardId);
            if (stored !== null) {
                setIsCollapsed(stored === 'true');
            }
            setIsInitialized(true);
        }
    }, [cardId]);

    // Persist state changes to localStorage (only after initialization)
    useEffect(() => {
        if (isInitialized && typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEY_PREFIX + cardId, String(isCollapsed));
        }
    }, [cardId, isCollapsed, isInitialized]);

    const toggleCollapsed = () => {
        setIsCollapsed(prev => !prev);
    };

    return (
        <div className={`collapsible-card panel ${className} ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            <div className="collapsible-card-header" onClick={toggleCollapsed}>
                <div className="collapsible-card-header-content">
                    <h3 className="collapsible-card-title">{title}</h3>
                    {subtitle && <span className="collapsible-card-subtitle">{subtitle}</span>}
                </div>
                {headerActions && (
                    <div className="collapsible-card-header-actions" onClick={(e) => e.stopPropagation()}>
                        {headerActions}
                    </div>
                )}
                <button
                    className="collapsible-card-toggle"
                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleCollapsed();
                    }}
                >
                    <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} style={{ color: 'var(--accent-color)' }} />
                </button>
            </div>
            {!isCollapsed && (
                <div className="collapsible-card-content">
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Hook to clear all stored card states (useful for debugging)
 */
export const useClearCardStates = () => {
    return () => {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(STORAGE_KEY_PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    };
};
